import React, { useState, useRef, useEffect } from 'react';
import { customersAPI } from '../../../lib/api';
import { getCurrentCompanyId, getCurrentCompanyName, getCurrentCompanyAddress, getCurrentCompanyLogo } from '../../../utils/auth';
import Icon from '../../../components/AppIcon';

const fmtNum = (val) => (val == null ? 'KES 0' : typeof val === 'number' ? `KES ${val.toLocaleString()}` : val);

const CustomerStatementModal = ({ isOpen, onClose, customer, startDate, endDate, setStartDate, setEndDate }) => {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef();

  const companyId   = getCurrentCompanyId();
  const companyName = getCurrentCompanyName();
  const companyLogo = getCurrentCompanyLogo();
  const companyAddr = getCurrentCompanyAddress();

  // Build address lines safely
  const addrLines = (() => {
    if (!companyAddr) return [];
    if (typeof companyAddr === 'string') return [companyAddr];
    return [
      companyAddr.street,
      [companyAddr.city, companyAddr.state, companyAddr.zipCode].filter(Boolean).join(', '),
      companyAddr.country,
      companyAddr.email,
      companyAddr.phone,
    ].filter(Boolean);
  })();

  const fetchStatement = async () => {
    if (!customer?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await customersAPI.getStatement(companyId, customer.id, startDate, endDate);
      if (res.data?.Success) {
        setStatement(res.data.Data);
      } else {
        setError(res.data?.Message || 'Failed to load statement');
      }
    } catch (err) {
      setError('Failed to load statement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && customer?.id) fetchStatement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customer?.id]);

  const handlePrint = () => {
    if (!statement) return;

    const logoHtml = companyLogo
      ? `<img src="${companyLogo}" alt="${companyName}" style="height:60px;width:auto;object-fit:contain;" />`
      : `<div style="width:60px;height:60px;border-radius:8px;background:#2563eb;display:flex;align-items:center;justify-content:center;font-size:26px;color:#fcd34d;font-weight:900;">${(companyName || 'C')[0]}</div>`;

    const txRows = (statement.Transactions || []).map((tx, i) => {
      let items = [];
      try { const parsed = JSON.parse(tx.JsonDetails); items = Array.isArray(parsed) ? parsed : []; } catch { items = []; }
      const itemsHtml = items.map(it =>
        `<div style="font-size:11px;color:#64748b;padding:1px 0;">${it.description} &times; ${it.quantity} @ ${Number(it.unit_price).toLocaleString()} = ${Number(it.amount).toLocaleString()}</div>`
      ).join('');
      return `<tr style="background:${i % 2 === 0 ? '#fff' : '#f8fafc'};">
        <td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;white-space:nowrap;color:#475569;font-size:13px;">${tx.Date}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;font-size:13px;">
          <div style="font-weight:600;color:#2563eb;">${tx.Description}</div>${itemsHtml}
        </td>
        <td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;text-align:right;font-weight:600;color:#dc2626;font-size:13px;">${tx.Debit}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;text-align:right;font-weight:600;color:#16a34a;font-size:13px;">${tx.Credit}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #e2e8f0;text-align:right;font-weight:700;color:#1e293b;font-size:13px;">${tx.Balance}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Statement - ${statement.CustomerName}</title>
  <style>
    @page { size: A4; margin: 16mm 14mm; }
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Segoe UI',Arial,sans-serif; color:#1e293b; background:#fff; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:18px; border-bottom:4px solid #2563eb; margin-bottom:22px; }
    .brand { display:flex; align-items:center; gap:14px; }
    .brand-name { font-size:21px; font-weight:800; color:#2563eb; }
    .brand-sub { font-size:11px; color:#64748b; margin-top:3px; }
    .brand-addr { font-size:11px; color:#94a3b8; margin-top:2px; }
    .doc-title { text-align:right; }
    .doc-title h2 { font-size:24px; font-weight:800; color:#1e293b; letter-spacing:1px; }
    .doc-title p { font-size:11px; color:#64748b; margin-top:4px; }
    .meta { display:grid; grid-template-columns:1fr 1fr; gap:18px; margin-bottom:22px; }
    .meta-box { background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:13px; }
    .meta-box h3 { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.8px; color:#94a3b8; margin-bottom:7px; }
    .meta-box p { font-size:13px; color:#1e293b; margin-bottom:3px; }
    .meta-box .sub { font-size:11px; color:#64748b; }
    .summary { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:22px; }
    .sc { border-radius:8px; padding:13px; text-align:center; }
    .sc.blue  { background:#eff6ff; border:1px solid #bfdbfe; }
    .sc.amber { background:#fffbeb; border:1px solid #fde68a; }
    .sc.green { background:#f0fdf4; border:1px solid #bbf7d0; }
    .sc.red   { background:#fef2f2; border:1px solid #fecaca; }
    .sc .amt  { font-size:15px; font-weight:800; margin-bottom:4px; }
    .sc.blue .amt { color:#2563eb; } .sc.amber .amt { color:#d97706; }
    .sc.green .amt { color:#16a34a; } .sc.red .amt { color:#dc2626; }
    .sc .lbl  { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.5px; color:#64748b; }
    table { width:100%; border-collapse:collapse; }
    thead tr { background:#1e293b; }
    thead th { padding:10px 12px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; color:#fff; }
    thead th:nth-child(n+3) { text-align:right; }
    tfoot tr { background:#0f172a; border-top:3px solid #2563eb; }
    tfoot td { padding:11px 12px; font-weight:700; font-size:13px; color:#fff; }
    tfoot td:nth-child(n+3) { text-align:right; }
    .foot { margin-top:28px; padding-top:14px; border-top:1px solid #e2e8f0; display:flex; justify-content:space-between; }
    .foot p { font-size:10px; color:#94a3b8; }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">
      ${logoHtml}
      <div>
        <div class="brand-name">${companyName || ''}</div>       
        ${addrLines.map(l => `<div class="brand-addr">${l}</div>`).join('')}
      </div>
    </div>
    <div class="doc-title">
      <h2>ACCOUNT STATEMENT</h2>
      <p>Generated: ${statement.GeneratedOn}</p>
      <p>Period: ${startDate} &ndash; ${endDate}</p>
    </div>
  </div>
  <div class="meta">
    <div class="meta-box">
      <h3>Bill To</h3>
      <p><strong>${statement.CustomerName}</strong></p>
      <p class="sub">${statement.CustomerEmail || ''}</p>
    </div>
    <div class="meta-box">
      <h3>Statement Period</h3>
      <p>${startDate} &ndash; ${endDate}</p>
      <p class="sub">Generated on ${statement.GeneratedOn}</p>
    </div>
  </div>
  <div class="summary">
    <div class="sc blue"><div class="amt">${fmtNum(statement.OpeningBalance)}</div><div class="lbl">Opening Balance</div></div>
    <div class="sc amber"><div class="amt">${fmtNum(statement.TotalInvoiced)}</div><div class="lbl">Total Invoiced</div></div>
    <div class="sc green"><div class="amt">${fmtNum(statement.TotalPaid)}</div><div class="lbl">Total Paid</div></div>
    <div class="sc red"><div class="amt">${fmtNum(statement.ClosingBalance)}</div><div class="lbl">Closing Balance</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Date</th><th>Description</th>
        <th style="text-align:right">Debit</th>
        <th style="text-align:right">Credit</th>
        <th style="text-align:right">Balance</th>
      </tr>
    </thead>
    <tbody>${txRows}</tbody>
    <tfoot>
      <tr>
        <td colspan="2">TOTALS</td>
        <td style="text-align:right;color:#fca5a5;">${fmtNum(statement.TotalInvoiced)}</td>
        <td style="text-align:right;color:#86efac;">${fmtNum(statement.TotalPaid)}</td>
        <td style="text-align:right">${fmtNum(statement.ClosingBalance)}</td>
      </tr>
    </tfoot>
  </table>
  <div class="foot">
    <p>This is a computer-generated statement and requires no signature.</p>
    <p>${companyName || ''} &bull; Generated ${statement.GeneratedOn}</p>
  </div>
</body>
</html>`;

    let iframe = document.getElementById('__stmt_print_frame__');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = '__stmt_print_frame__';
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
      document.body.appendChild(iframe);
    }
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.contentWindow.focus();
    setTimeout(() => iframe.contentWindow.print(), 600);
  };

  if (!isOpen) return null;

  const parseItems = (json) => { try { const p = JSON.parse(json); return Array.isArray(p) ? p : []; } catch { return []; } };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-2 pt-16 pb-2">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[98vw] h-full flex flex-col overflow-hidden">

        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Customer Statement</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button onClick={fetchStatement} disabled={loading}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Loading...' : 'Generate'}
          </button>
          {statement && (
            <button onClick={handlePrint}
              className="px-4 py-1.5 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900 flex items-center gap-1.5">
              <span>Print / PDF</span>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>}
          {!statement && !loading && !error && (
            <div className="text-center text-gray-400 py-16 text-sm">Select a date range and click Generate.</div>
          )}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent"></div>
            </div>
          )}

          {statement && (
            <div ref={printRef}>
              {/* Company header — matches invoice style */}
              <div className="flex items-start justify-between mb-8 pb-6 border-b-4 border-blue-600">
                <div className="flex items-center gap-4">
                  {companyLogo ? (
                    <img src={companyLogo} alt={companyName} className="h-16 w-auto object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-16 h-16 rounded-md flex items-center justify-center bg-blue-600">
                      <Icon name="Building2" size={32} color="#FCD34D" />
                    </div>
                  )}
                  <div>
                    <h1 className="text-2xl font-extrabold text-blue-600">{companyName}</h1>
                    {addrLines.map((l, i) => <p key={i} className="text-xs text-gray-400">{l}</p>)}
                  </div>
                </div>
                <div className="text-right">
                  <h2 className="text-xl font-bold text-gray-800">Account Statement</h2>
                  <p className="text-xs text-gray-500 mt-1">Generated: {statement.GeneratedOn}</p>
                  <p className="text-xs text-gray-500">Period: {startDate} – {endDate}</p>
                  <div className="mt-2">
                    <p className="text-sm font-semibold text-gray-700">{statement.CustomerName}</p>
                    <p className="text-xs text-gray-400">{statement.CustomerEmail}</p>
                  </div>
                </div>
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Opening Balance', value: fmtNum(statement.OpeningBalance), color: 'bg-blue-50 border-blue-200 text-blue-700' },
                  { label: 'Total Invoiced',  value: fmtNum(statement.TotalInvoiced),  color: 'bg-amber-50 border-amber-200 text-amber-700' },
                  { label: 'Total Paid',      value: fmtNum(statement.TotalPaid),      color: 'bg-green-50 border-green-200 text-green-700' },
                  { label: 'Closing Balance', value: fmtNum(statement.ClosingBalance), color: 'bg-red-50 border-red-200 text-red-700' },
                ].map((c) => (
                  <div key={c.label} className={`rounded-lg border p-4 text-center ${c.color}`}>
                    <div className="text-lg font-extrabold">{c.value}</div>
                    <div className="text-xs font-semibold uppercase tracking-wide mt-1 opacity-70">{c.label}</div>
                  </div>
                ))}
              </div>

              {/* Transactions table */}
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="px-3 py-3 text-left text-xs uppercase tracking-wide">Date</th>
                    <th className="px-3 py-3 text-left text-xs uppercase tracking-wide">Description</th>
                    <th className="px-3 py-3 text-right text-xs uppercase tracking-wide">Debit</th>
                    <th className="px-3 py-3 text-right text-xs uppercase tracking-wide">Credit</th>
                    <th className="px-3 py-3 text-right text-xs uppercase tracking-wide">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {(statement.Transactions || []).map((tx, i) => {
                    const items = parseItems(tx.JsonDetails);
                    return (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-3 border-b border-gray-100 whitespace-nowrap text-gray-600">{tx.Date}</td>
                        <td className="px-3 py-3 border-b border-gray-100">
                          <div className="font-semibold text-blue-600">{tx.Description}</div>
                          {items.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {items.map((item, j) => (
                                <div key={j} className="text-xs text-gray-500">
                                  {item.description} &times; {item.quantity} @ {item.unit_price?.toLocaleString()} = {item.amount?.toLocaleString()}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-100 text-right font-semibold text-red-600">{tx.Debit}</td>
                        <td className="px-3 py-3 border-b border-gray-100 text-right font-semibold text-green-600">{tx.Credit}</td>
                        <td className="px-3 py-3 border-b border-gray-100 text-right font-bold text-gray-800">{tx.Balance}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-900 text-white border-t-4 border-blue-500">
                    <td colSpan={2} className="px-3 py-3.5 font-bold text-sm tracking-wide">TOTALS</td>
                    <td className="px-3 py-3.5 text-right font-bold text-red-300 text-sm">{fmtNum(statement.TotalInvoiced)}</td>
                    <td className="px-3 py-3.5 text-right font-bold text-green-300 text-sm">{fmtNum(statement.TotalPaid)}</td>
                    <td className="px-3 py-3.5 text-right font-extrabold text-white text-sm">{fmtNum(statement.ClosingBalance)}</td>
                  </tr>
                </tfoot>
              </table>

              <div className="mt-10 pt-5 border-t border-gray-200 flex justify-between items-center">
                <p className="text-xs text-gray-400">This is a computer-generated statement and requires no signature.</p>
                <p className="text-xs text-gray-400">Generated on {statement.GeneratedOn}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerStatementModal;
